import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { BottomNav } from "@/components/shared/Navigation";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Navigation, Calendar, Clock, Star, Gift,
  Loader2, Sparkles, Check, X, Users, Coffee, UtensilsCrossed,
  IceCream2, ShoppingBag, Palette, Clapperboard, Dumbbell,
  Map as MapIcon, List, Save, Lock, Wand2, ChevronUp, ChevronDown,
  Plus, Car, CalendarPlus, Image as ImageIcon, Zap, PartyPopper,
  ExternalLink, Route, Copy, Play, Square, Timer, Trophy,
  Download, Link2, ChevronLeft, ChevronRight, Award, Shield,
  SlidersHorizontal, Crosshair, Bookmark, Gauge,
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import confetti from "canvas-confetti";
import html2canvas from "html2canvas";
import ItineraryPlanner from "@/components/itinerary/ItineraryPlanner";
import OutOfPocketEstimator from "@/components/itinerary/OutOfPocketEstimator";
import RoutePreviewMap from "@/components/itinerary/RoutePreviewMap";
import RehearseBirthday from "@/components/itinerary/RehearseBirthday";
import ChangePaceSheet from "@/components/itinerary/ChangePaceSheet";
import BrowseOffers from "@/components/itinerary/BrowseOffers";
import DragSlotScheduler from "@/components/itinerary/DragSlotScheduler";
import TimelineScheduler from "@/components/itinerary/TimelineScheduler";
import SmartQueueScheduler from "@/components/itinerary/SmartQueueScheduler";
import TempoSelector from "@/components/itinerary/TempoSelector";
import TooltipOverlay from "@/components/shared/TooltipOverlay";

// ── Theme Color Utility ───────────────────────────────────────
const tc = (hex, alpha = 1) => {
  if (!hex || hex.length < 7) return alpha < 1 ? `rgba(236,72,153,${alpha})` : "rgb(236,72,153)";
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return alpha < 1 ? `rgba(${r},${g},${b},${alpha})` : `rgb(${r},${g},${b})`;
};
const darken = (hex, amt = 30) => {
  if (!hex || hex.length < 7) return hex;
  return `rgb(${Math.max(0, parseInt(hex.slice(1, 3), 16) - amt)},${Math.max(0, parseInt(hex.slice(3, 5), 16) - amt)},${Math.max(0, parseInt(hex.slice(5, 7), 16) - amt)})`;
};

const CATEGORY_OPTIONS = [
  { id: "Coffee & Tea", label: "Coffee", icon: Coffee, dbCats: ["Coffee", "Coffee/Cafe", "Boba/Tea"] },
  { id: "Fast Food", label: "Fast Food", icon: UtensilsCrossed, dbCats: ["Fast Food", "Fast Casual"] },
  { id: "Casual Dining", label: "Dining", icon: UtensilsCrossed, dbCats: ["Restaurant", "Restaurant/Buffet", "Restaurant/Wine Bar"] },
  { id: "Ice Cream & Dessert", label: "Dessert", icon: IceCream2, dbCats: ["Dessert", "Dessert/Ice Cream", "Ice Cream / Dessert", "Snack"] },
  { id: "Bakery / Dessert", label: "Bakery", icon: Coffee, dbCats: ["Bakery", "Bakery/Cafe"] },
  { id: "Retail", label: "Retail", icon: ShoppingBag, dbCats: ["Retail", "Grocery", "Market/Specialty", "Convenience"] },
  { id: "Clothing", label: "Clothing", icon: ShoppingBag, dbCats: ["Clothing", "Jewelry"] },
  { id: "Beauty", label: "Beauty", icon: Palette, dbCats: ["Cosmetics", "Entertainment/Spa"] },
  { id: "Entertainment", label: "Fun", icon: Clapperboard, dbCats: ["Entertainment"] },
  { id: "Fitness", label: "Fitness", icon: Dumbbell, dbCats: ["Service"] },
];

// Expand curated category IDs to actual DB category names
const expandCategories = (selectedIds) => {
  const dbCats = new Set();
  selectedIds.forEach(id => {
    const opt = CATEGORY_OPTIONS.find(c => c.id === id);
    if (opt) opt.dbCats.forEach(dc => dbCats.add(dc));
    else dbCats.add(id); // pass through if not in mapping
  });
  return [...dbCats];
};

const TRIP_TYPES = [
  { id: "half_day", label: "Half Day", desc: "3-4 stops" },
  { id: "full_day", label: "Full Day", desc: "6-8 stops" },
  { id: "multi_day", label: "Multi-Day", desc: "Up to 10 days" },
  { id: "month", label: "Month", desc: "Spread it out" },
];

const SLOT_COLORS = {
  morning: { text: "text-amber-700", label: "Morning" },
  lunch: { text: "text-orange-700", label: "Lunch" },
  afternoon: { text: "text-sky-700", label: "Afternoon" },
  evening: { text: "text-indigo-700", label: "Evening" },
};
const SLOT_TIMES = { morning: "8:00 AM", lunch: "12:00 PM", afternoon: "2:30 PM", evening: "6:00 PM" };
const CAT_COLORS = {
  "Coffee & Tea": "bg-amber-400", "Coffee": "bg-amber-400", "Coffee/Cafe": "bg-amber-400", "Boba/Tea": "bg-amber-400",
  "Fast Food": "bg-red-400", "Fast Casual": "bg-red-400",
  "Casual Dining": "bg-orange-400", "Restaurant": "bg-orange-500", "Restaurant/Buffet": "bg-orange-500", "Restaurant/Wine Bar": "bg-orange-500",
  "Ice Cream & Dessert": "bg-pink-400", "Dessert": "bg-pink-400", "Dessert/Ice Cream": "bg-pink-400", "Ice Cream / Dessert": "bg-pink-400", "Snack": "bg-pink-300",
  "Retail": "bg-violet-400", "Grocery": "bg-violet-300", "Market/Specialty": "bg-violet-300", "Convenience": "bg-violet-300",
  "Beauty": "bg-fuchsia-400", "Cosmetics": "bg-fuchsia-400", "Entertainment/Spa": "bg-fuchsia-400",
  "Entertainment": "bg-sky-400",
  "Fitness": "bg-emerald-400", "Service": "bg-emerald-300",
  "Clothing": "bg-indigo-400", "Jewelry": "bg-indigo-300",
  "Bakery / Dessert": "bg-rose-400", "Bakery": "bg-rose-400", "Bakery/Cafe": "bg-rose-400",
  "Drink": "bg-cyan-400", "Food": "bg-orange-400", "Pet": "bg-lime-400", "Other": "bg-slate-400",
};
const EST_DURATION = {
  "Coffee & Tea": 10, "Coffee": 10, "Coffee/Cafe": 10, "Boba/Tea": 10,
  "Bakery / Dessert": 10, "Bakery": 10, "Bakery/Cafe": 10,
  "Fast Food": 15, "Fast Casual": 15,
  "Casual Dining": 30, "Restaurant": 30, "Restaurant/Buffet": 30, "Restaurant/Wine Bar": 30,
  "Ice Cream & Dessert": 12, "Dessert": 12, "Dessert/Ice Cream": 12, "Ice Cream / Dessert": 12, "Snack": 10,
  "Retail": 20, "Grocery": 15, "Market/Specialty": 15, "Convenience": 10,
  "Clothing": 20, "Jewelry": 15,
  "Beauty": 25, "Cosmetics": 25, "Entertainment/Spa": 30,
  "Entertainment": 45, "Fitness": 30, "Service": 20,
  "Drink": 15, "Food": 15, "Pet": 15, "Other": 15,
};
const getEstMin = (cat) => EST_DURATION[cat] || 15;
const fmtMin = (m) => { const h = Math.floor(m / 60); const mm = Math.round(m % 60); return h > 0 ? `${h}h ${String(mm).padStart(2, "0")}m` : `${mm}m`; };
const fmtTimer = (ms) => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

// ── Category Balance Bar ──────────────────────────────────────
const CategoryBalanceBar = ({ stops }) => {
  if (!stops?.length) return null;
  const counts = {};
  stops.forEach(s => { counts[s.category] = (counts[s.category] || 0) + 1; });
  const total = stops.length;
  const cats = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="category-balance-bar">
      <div className="flex h-2 rounded-full overflow-hidden">
        {cats.map(([cat, count]) => (
          <div key={cat} className={`${CAT_COLORS[cat] || "bg-slate-400"} transition-all`} style={{ width: `${(count / total) * 100}%` }} title={`${cat}: ${count}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
        {cats.map(([cat, count]) => (
          <span key={cat} className="flex items-center gap-1 text-[9px] text-slate-500">
            <span className={`w-2 h-2 rounded-full ${CAT_COLORS[cat] || "bg-slate-400"}`} /> {cat} ({count})
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Travel Segment ────────────────────────────────────────────
const TravelSegment = ({ segment }) => {
  if (!segment) return null;
  return (
    <div className="flex items-center gap-1.5 ml-4 my-1" data-testid="travel-segment">
      <Car className="w-3 h-3 text-slate-300" />
      <span className="text-[9px] text-slate-400 font-medium">{segment.duration_min} min · {segment.distance_mi} mi</span>
    </div>
  );
};

// ── Accordion Section ─────────────────────────────────────────
const AccordionSection = ({ id, title, icon: Icon, badge, isOpen, onToggle, themeColor, children }) => (
  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`accordion-${id}`}>
    <button onClick={onToggle} className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-slate-50/50 transition" data-testid={`accordion-btn-${id}`}>
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: themeColor }} />
      <span className="flex-1 text-left text-sm font-bold text-slate-700">{title}</span>
      {badge}
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
          <div className="px-4 pb-4 pt-1">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ── Google Calendar Export ─────────────────────────────────────
function exportToGoogleCalendar(stops, userBirthday) {
  const now = new Date();
  const bday = userBirthday ? new Date(userBirthday) : null;
  let baseDate = now;
  if (bday) { bday.setFullYear(now.getFullYear()); if (bday < now) bday.setFullYear(now.getFullYear() + 1); baseDate = bday; }
  const dateStr = baseDate.toISOString().split("T")[0].replace(/-/g, "");
  const slotHours = { morning: "08", lunch: "12", afternoon: "14", evening: "18" };
  stops.forEach((stop, i) => {
    const hour = slotHours[stop.time_slot] || "12";
    const startTime = `${dateStr}T${hour}${String((i * 5) % 60).padStart(2, "0")}00`;
    const endHour = String(parseInt(hour) + 1).padStart(2, "0");
    const endTime = `${dateStr}T${endHour}${String((i * 5) % 60).padStart(2, "0")}00`;
    const title = encodeURIComponent(`Birthday Deal: ${stop.name}`);
    const details = encodeURIComponent(`${stop.offer_title}\nValue: $${stop.value_est}\nEase: ${stop.ease_stars}/5 stars`);
    window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startTime}/${endTime}&details=${details}`, "_blank");
  });
}

// ── Share Text Generator ──────────────────────────────────────
function generateShareText(itinerary) {
  if (!itinerary) return "";
  let text = `My BirthdayDeaLight Itinerary\n$${itinerary.total_savings} in free stuff | ${itinerary.total_stops} stops\n`;
  if (itinerary.route_optimized) text += `${itinerary.total_travel_min} min total drive | ${itinerary.total_distance_mi} mi\n`;
  text += "\n";
  for (const slot of ["morning", "lunch", "afternoon", "evening"]) {
    const stops = itinerary.stops.filter(s => s.time_slot === slot);
    if (stops.length === 0) continue;
    text += `${SLOT_COLORS[slot]?.label || slot}\n`;
    stops.forEach(s => { text += `  - ${s.name}: ${s.offer_title} ($${s.value_est})\n`; });
    text += "\n";
  }
  return text;
}

// ════════════════════════════════════════════════════════════════
export default function ItineraryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getStatus } = useUnifiedStatus();
  const themeColor = user?.favorite_color || "#ec4899";

  // ── State ─────────────────────────────────────────────────
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [addingStop, setAddingStop] = useState(null);
  const confettiFired = useRef(false);
  const shareRef = useRef(null);
  const [stopTimers, setStopTimers] = useState({});
  const [timerTick, setTimerTick] = useState(0);
  const [tripTimer, setTripTimer] = useState(null); // { startedAt: Date, endedAt: Date|null }
  const [activeDay, setActiveDay] = useState(1);
  const [shareLink, setShareLink] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);

  // New state for restructured UX
  const [openSection, setOpenSection] = useState(null); // 'timeline' | 'go' | 'preflight'
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showRadiusPicker, setShowRadiusPicker] = useState(false);
  const [radiusValue, setRadiusValue] = useState(user?.search_radius || 10);
  const [prepDone, setPrepDone] = useState({}); // Loaded from backend, keyed by "stopId-action"
  const [showQR, setShowQR] = useState(false);
  const [scheduleTab, setScheduleTab] = useState(null); // "slots" | "timeline" | "queue" | null
  const [startDate, setStartDate] = useState(""); // ISO date string for itinerary start
  const [tempoStep, setTempoStep] = useState(false); // true = show tempo selector (pre-step)
  const [collectionTempo, setCollectionTempo] = useState(null); // one_big_day | birthday_week | birthday_month | custom
  const [paceSheetOpen, setPaceSheetOpen] = useState(false);
  const [customDates, setCustomDates] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistStep, setWishlistStep] = useState("loading"); // loading | show | done

  // Compute next birthday for smart preset
  const getNextBirthday = useCallback(() => {
    if (!user?.birthday) return "";
    const today = new Date();
    const bday = new Date(user.birthday + "T00:00:00");
    const thisYear = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
    const target = thisYear >= today ? thisYear : new Date(today.getFullYear() + 1, bday.getMonth(), bday.getDate());
    return target.toISOString().split("T")[0];
  }, [user?.birthday]);

  // Initialize start date to birthday
  useEffect(() => { if (!startDate && user?.birthday) setStartDate(getNextBirthday()); }, [startDate, user?.birthday, getNextBirthday]);

  const [selectedCategories, setSelectedCategories] = useState(["Coffee & Tea", "Fast Food", "Casual Dining", "Ice Cream & Dessert"]);
  const [tripType, setTripType] = useState("full_day");
  const [numDays, setNumDays] = useState(1);
  const [city, setCity] = useState(user?.city || "Sacramento");
  const [state, setState] = useState(user?.state || "CA");
  const [detectingGPS, setDetectingGPS] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);

  const toggleSection = (id) => setOpenSection(prev => prev === id ? null : id);

  // ── Load Itinerary ────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const itRes = await axios.get(`${API}/itinerary/me`);
        if (itRes.data) {
          setItinerary(itRes.data);
          if (itRes.data.categories_selected?.length) setSelectedCategories(itRes.data.categories_selected);
          setTripType(itRes.data.trip_type || "full_day");
          setNumDays(itRes.data.num_days || 1);
          // Use itinerary location data (user override handled by separate effect below)
          setCity(itRes.data.location?.city || "Sacramento");
          setState(itRes.data.location?.state || "CA");
          setRadiusValue(itRes.data.radius_mi || 10);
          if (itRes.data.collection_tempo) setCollectionTempo(itRes.data.collection_tempo);
          if (itRes.data.custom_dates?.length) setCustomDates(itRes.data.custom_dates);
          // Load persisted prep actions into prepDone map
          const loaded = {};
          for (const stop of itRes.data.stops || []) {
            if (stop.prep_actions) {
              for (const [key, val] of Object.entries(stop.prep_actions)) {
                if (val?.completed) loaded[key] = true;
              }
            }
          }
          setPrepDone(loaded);
        }
      } catch {} finally { setLoading(false); }
    })();
    // Load wishlist for pre-population step
    (async () => {
      try {
        const wlRes = await axios.get(`${API}/wishlist`);
        if (wlRes.data.items?.length > 0) {
          setWishlistItems(wlRes.data.items.map(i => ({ ...i, selected: true })));
          setWishlistStep("show");
        } else {
          setWishlistStep("done");
        }
      } catch { setWishlistStep("done"); }
    })();
  }, []);

  // Sync location & radius from user settings (takes priority over stale itinerary data)
  useEffect(() => {
    if (user?.city) setCity(user.city);
    if (user?.state) setState(user.state);
    if (user?.search_radius) setRadiusValue(user.search_radius);
  }, [user?.city, user?.state, user?.search_radius]);

  const fetchSuggestions = useCallback(async () => {
    if (!itinerary) return;
    try { const res = await axios.get(`${API}/itinerary/suggestions`); setSuggestions(res.data.suggestions || []); } catch {}
  }, [itinerary]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  // Confetti on all claimed
  useEffect(() => {
    if (!itinerary || openSection !== "go") return;
    const allClaimed = itinerary.stops.length > 0 && itinerary.stops.every(s => s.claimed);
    if (allClaimed && !confettiFired.current) {
      confettiFired.current = true;
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
      toast.success("You claimed ALL your birthday deals! Happy Birthday!", { duration: 5000 });
    }
    if (!allClaimed) confettiFired.current = false;
  }, [itinerary, openSection]);

  // Live timer tick for trip timer
  useEffect(() => {
    if (openSection !== "go" || !tripTimer?.startedAt || tripTimer?.endedAt) return;
    const iv = setInterval(() => setTimerTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, [openSection, tripTimer]);

  // ── Handlers ──────────────────────────────────────────────

  const detectGPS = async () => {
    if (!navigator.geolocation) return toast.error("GPS not available");
    setDetectingGPS(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGpsCoords({ lat, lng });
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const data = await res.json();
          const c = data.address?.city || data.address?.town || data.address?.village || "";
          const s = data.address?.state || "";
          if (c) { setCity(c); setState(s); toast.success(`Location: ${c}, ${s}`); }
        } catch { toast.error("Could not detect location"); } finally { setDetectingGPS(false); }
      },
      () => { toast.error("Location access denied"); setDetectingGPS(false); },
      { timeout: 10000 }
    );
  };

  const generateItinerary = async () => {
    if (selectedCategories.length === 0) return toast.error("Select at least one category");
    if (!city.trim()) return toast.error("Please enter a city");
    setGenerating(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const payload = {
        categories: expandCategories(selectedCategories),
        trip_type: tripType,
        num_days: numDays,
        city,
        state,
        radius_mi: radiusValue,
        start_date: startDate || undefined,
        collection_tempo: collectionTempo || "one_big_day",
        custom_dates: collectionTempo === "custom" ? customDates : [],
      };
      // Send GPS coords if available (takes priority over city name on backend)
      if (gpsCoords) {
        payload.lat = gpsCoords.lat;
        payload.lng = gpsCoords.lng;
      }
      const res = await axios.post(`${API}/itinerary/generate`, payload, { timeout: 90000 });
      setItinerary(res.data);
      setDirty(false);
      setShowFilterModal(false);
      setSuggestions([]);
      const routeMsg = res.data.route_optimized ? ` Route: ${res.data.total_travel_min}min, ${res.data.total_distance_mi}mi` : "";
      toast.success(`${res.data.total_stops} stops, $${res.data.total_savings} in birthday deals!${routeMsg}`);
      fetchSuggestions();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to generate";
      toast.error(err?.code === "ECONNABORTED" || msg.includes("timeout") ? "Taking too long -- try fewer categories" : `Generation failed: ${msg}`);
    } finally { setGenerating(false); }
  };

  const saveItinerary = async () => {
    if (!itinerary) return; setSaving(true);
    try { await axios.put(`${API}/itinerary/save`, { stops: itinerary.stops, trip_type: tripType, num_days: numDays, start_date: startDate || undefined, collection_tempo: collectionTempo || itinerary?.collection_tempo }); setDirty(false); toast.success("Saved!"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const removeStop = (stopId) => {
    if (!itinerary) return;
    const newStops = itinerary.stops.filter(s => s.id !== stopId).map((s, i) => ({ ...s, order: i + 1 }));
    setItinerary({ ...itinerary, stops: newStops, total_stops: newStops.length, total_savings: Math.round(newStops.reduce((sum, s) => sum + (s.value_est || 0), 0) * 100) / 100 });
    setDirty(true);
  };

  const moveStop = (idx, dir) => {
    if (!itinerary) return;
    const stops = [...itinerary.stops]; const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= stops.length) return;
    [stops[idx], stops[newIdx]] = [stops[newIdx], stops[idx]];
    stops.forEach((s, i) => s.order = i + 1);
    setItinerary({ ...itinerary, stops });
    setDirty(true);
  };

  const toggleClaim = async (stopId) => {
    if (!itinerary) return;
    const stop = itinerary.stops.find(s => s.id === stopId);
    if (!stop?.claimed && stopTimers[stopId]?.startedAt && !stopTimers[stopId]?.endedAt) {
      setStopTimers(prev => ({ ...prev, [stopId]: { ...prev[stopId], endedAt: new Date() } }));
    }
    setItinerary({ ...itinerary, stops: itinerary.stops.map(s => s.id === stopId ? { ...s, claimed: !s.claimed } : s) });
    try { await axios.post(`${API}/itinerary/stop/${stopId}/claim`); } catch {}
  };

  const addSuggestion = async (businessId) => {
    setAddingStop(businessId);
    try {
      const res = await axios.post(`${API}/itinerary/add-stop`, { business_id: businessId });
      setItinerary({ ...itinerary, stops: [...(itinerary?.stops || []), res.data.stop], total_stops: (itinerary?.total_stops || 0) + 1, total_savings: res.data.total_savings });
      setSuggestions(prev => prev.filter(s => s.business_id !== businessId));
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); } finally { setAddingStop(null); }
  };

  const openGoogleMaps = (stop) => {
    const q = stop.lat && stop.lng ? `${stop.lat},${stop.lng}` : encodeURIComponent(`${stop.name} ${city} ${state}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank");
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
      }
      return true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { return false; }
      document.body.removeChild(ta);
      return true;
    }
  };

  const copyShareText = async () => {
    const ok = await copyToClipboard(generateShareText(itinerary));
    toast[ok ? "success" : "error"](ok ? "Copied to clipboard!" : "Copy failed");
  };

  const saveAsImage = async () => {
    if (!shareRef.current) return;
    setSavingImage(true);
    try {
      const canvas = await html2canvas(shareRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      const link = document.createElement("a");
      link.download = "my-birthday-itinerary.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image saved!");
    } catch { toast.error("Could not save image"); }
    finally { setSavingImage(false); }
  };

  const generateShareLink = async () => {
    setSharing(true);
    try {
      const res = await axios.post(`${API}/itinerary/share`);
      const link = `${window.location.origin}/shared-itinerary/${res.data.share_id}`;
      setShareLink(link);
      const ok = await copyToClipboard(link);
      toast.success(ok ? "Share link copied!" : "Link generated! Tap to copy.");
    } catch { toast.error("Could not generate share link"); }
    finally { setSharing(false); }
  };

  const updateRadius = async (newRadius) => {
    setRadiusValue(newRadius);
    try {
      await axios.put(`${API}/auth/location`, { city, state, search_radius: newRadius });
      toast.success(`Radius updated to ${newRadius} mi`);
      setShowRadiusPicker(false);
    } catch { toast.error("Failed to update radius"); }
  };

  // ── Derived ───────────────────────────────────────────────
  const claimedCount = itinerary?.stops?.filter(s => s.claimed).length || 0;
  const totalStops = itinerary?.total_stops || 0;
  const nextUnclaimed = itinerary?.stops?.find(s => !s.claimed);

  // Readiness score: % of all prep steps completed across all stops
  const readinessData = useMemo(() => {
    if (!itinerary?.stops) return { pct: 100, total: 0, done: 0, remaining: [] };
    let total = 0;
    let done = 0;
    const remaining = [];
    for (const stop of itinerary.stops) {
      const pills = [];
      if (stop.requires_app) pills.push({ key: `${stop.id}-app`, label: "Download app", stop: stop.name });
      if (stop.signup_lead_days > 0) pills.push({ key: `${stop.id}-signup`, label: "Sign up ahead", stop: stop.name });
      if (stop.purchase_required) pills.push({ key: `${stop.id}-purchase`, label: "Purchase info", stop: stop.name });
      total += pills.length;
      for (const p of pills) {
        if (prepDone[p.key]) {
          done++;
        } else {
          remaining.push(p);
        }
      }
    }
    return { pct: total === 0 ? 100 : Math.round((done / total) * 100), total, done, remaining };
  }, [itinerary?.stops, prepDone]);
  const getSegment = (fromIdx) => itinerary?.travel_segments?.find(t => t.from_stop === fromIdx);
  const claimedSavings = Math.round((itinerary?.stops?.filter(s => s.claimed).reduce((sum, s) => sum + (s.value_est || 0), 0) || 0) * 100) / 100;
  const totalEstMin = (itinerary?.stops?.reduce((sum, s) => sum + getEstMin(s.category), 0) || 0) + (itinerary?.total_travel_min || 0);
  const tripElapsed = tripTimer?.startedAt ? ((tripTimer.endedAt || new Date()) - tripTimer.startedAt) : 0;

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: themeColor }} /></div>;

  // ════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="itinerary-page">
      {/* ═══ HEADER ═══ */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/profile")} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="itinerary-back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-slate-800">My Bday Itinerary</h1>
            {itinerary && (
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setShowFilterModal(true)} className="text-[10px] underline underline-offset-2 cursor-pointer" style={{ color: themeColor }} data-testid="change-location-btn">{city}{state ? `, ${state}` : ""}</button>
                {itinerary.route_optimized && <span className="text-[9px] bg-emerald-100 text-emerald-600 font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Route className="w-2.5 h-2.5" />Optimized</span>}
                <button onClick={() => setShowRadiusPicker(true)} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 cursor-pointer hover:opacity-80 transition" style={{ backgroundColor: tc(themeColor, 0.12), color: themeColor }} data-testid="radius-badge"><MapIcon className="w-2.5 h-2.5" />{radiusValue}mi</button>
              </div>
            )}
          </div>
          {dirty && (
            <button onClick={saveItinerary} disabled={saving} className="px-3 py-1.5 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-sm" style={{ backgroundColor: '#10b981' }} data-testid="save-itinerary-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          )}
        </div>
      </div>

      {/* ═══ EMPTY STATE → WISHLIST → TEMPO → PLANNER ═══ */}
      {!itinerary ? (
        <div data-testid="itinerary-empty">
          {/* Wishlist pre-population step */}
          {wishlistStep === "show" && !collectionTempo && wishlistItems.length > 0 ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="px-4 pt-4" data-testid="wishlist-prepopulate">
              <div className="bg-white rounded-2xl border border-violet-200 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-violet-500 to-indigo-600 px-4 py-3.5 flex items-center gap-3">
                  <Bookmark className="w-5 h-5 text-white" />
                  <div>
                    <p className="text-white font-bold text-sm">You have {wishlistItems.length} deal{wishlistItems.length !== 1 ? "s" : ""} on your wishlist</p>
                    <p className="text-violet-200 text-xs">Add them to your plan?</p>
                  </div>
                </div>
                <div className="p-3 max-h-64 overflow-y-auto space-y-2">
                  {wishlistItems.map(item => (
                    <label
                      key={item.business_id}
                      className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-colors ${item.selected ? "bg-violet-50 border border-violet-200" : "bg-slate-50 border border-slate-100"}`}
                      data-testid={`wishlist-prepop-${item.business_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => setWishlistItems(prev => prev.map(w => w.business_id === item.business_id ? { ...w, selected: !w.selected } : w))}
                        className="w-4 h-4 rounded accent-violet-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{item.name || item.business_name}</p>
                        <p className="text-xs text-slate-400 truncate">{item.offer_title}</p>
                      </div>
                      {item.offer_value_est > 0 && (
                        <span className="text-xs font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">${item.offer_value_est}</span>
                      )}
                    </label>
                  ))}
                </div>
                <div className="p-3 pt-0 flex gap-2">
                  <button
                    onClick={() => setWishlistStep("done")}
                    className="flex-1 py-2.5 text-xs font-semibold text-slate-500 bg-slate-100 rounded-xl hover:bg-slate-200 transition"
                    data-testid="wishlist-prepop-skip"
                  >
                    Skip
                  </button>
                  <button
                    onClick={() => {
                      const selected = wishlistItems.filter(w => w.selected);
                      if (selected.length > 0) {
                        // Store selected wishlist items for the planner to use
                        sessionStorage.setItem("wishlist_prepop", JSON.stringify(selected.map(w => w.business_id)));
                      }
                      setWishlistStep("done");
                    }}
                    className="flex-1 py-2.5 text-xs font-bold text-white rounded-xl transition"
                    style={{ background: `linear-gradient(135deg, ${themeColor}, ${darken(themeColor, 30)})` }}
                    data-testid="wishlist-prepop-add"
                  >
                    Add {wishlistItems.filter(w => w.selected).length} to Plan
                  </button>
                </div>
              </div>
            </motion.div>
          ) : !collectionTempo ? (
            <TempoSelector
              userColor={themeColor}
              birthday={getNextBirthday()}
              onSelect={({ tempo, customDates: dates }) => {
                setCollectionTempo(tempo);
                setCustomDates(dates || []);
                // Auto-set trip type & num_days based on tempo
                if (tempo === "one_big_day") { setTripType("full_day"); setNumDays(1); }
                else if (tempo === "birthday_week") { setTripType("multi_day"); setNumDays(7); }
                else if (tempo === "birthday_month") { setTripType("month"); setNumDays(30); }
                else if (tempo === "custom") { setTripType("multi_day"); setNumDays(dates?.length || 3); }
              }}
            />
          ) : (
            <ItineraryPlanner user={user} onGenerate={(data) => { setItinerary(data); }} />
          )}
        </div>
      ) : (
        <>
          {/* ═══ SAVINGS SCOREBOARD ═══ */}
          <div className="px-4 pt-3">
            <div className="rounded-2xl p-4 text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${themeColor}, ${darken(themeColor, 40)})` }} data-testid="savings-scoreboard">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-semibold text-white/70 uppercase tracking-wider">Your Birthday Haul</p>
                  <p className="text-3xl font-black mt-0.5">${itinerary.total_savings}</p>
                  <p className="text-xs text-white/70 mt-0.5">in free stuff</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2"><span className="text-2xl font-black">{totalStops}</span><span className="text-xs text-white/70">stops</span></div>
                  {claimedCount > 0 && <div className="bg-white/20 rounded-full px-2.5 py-1 text-[10px] font-bold" data-testid="claimed-progress">{claimedCount}/{totalStops} claimed</div>}
                </div>
              </div>

              {/* Running savings + Time stats */}
              <div className="mt-3 grid grid-cols-3 gap-2">
                {/* Claimed savings so far */}
                <div className="bg-white/15 rounded-xl px-2.5 py-2 text-center">
                  <p className="text-lg font-black" data-testid="running-savings">${claimedSavings}</p>
                  <p className="text-[8px] text-white/60">claimed</p>
                </div>
                {/* Estimated time */}
                <div className="bg-white/15 rounded-xl px-2.5 py-2 text-center">
                  <p className="text-lg font-black" data-testid="est-time">{fmtMin(totalEstMin)}</p>
                  <p className="text-[8px] text-white/60">est. time</p>
                </div>
                {/* Actual / driving */}
                <div className="bg-white/15 rounded-xl px-2.5 py-2 text-center">
                  {tripTimer?.startedAt ? (
                    <>
                      <p className="text-lg font-black" data-testid="actual-time">{fmtTimer(tripElapsed)}</p>
                      <p className="text-[8px] text-white/60">{tripTimer.endedAt ? "actual" : "running"}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-black">{fmtMin(itinerary.total_travel_min || 0)}</p>
                      <p className="text-[8px] text-white/60">drive time</p>
                    </>
                  )}
                </div>
              </div>

              {claimedCount > 0 && (
                <div className="mt-2.5 h-1.5 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-white rounded-full transition-all" style={{ width: `${(claimedCount / totalStops) * 100}%` }} /></div>
              )}
              {/* Readiness Score */}
              {readinessData.total > 0 && (
                <div className="mt-2.5" data-testid="readiness-score">
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-white/70 font-medium">Plan readiness</span>
                    <span className="font-bold text-white">{readinessData.pct}% ready</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${readinessData.pct}%`, backgroundColor: readinessData.pct === 100 ? "#4ade80" : "#fbbf24" }} />
                  </div>
                  {readinessData.pct < 100 && readinessData.remaining.length > 0 && (
                    <p className="text-[9px] text-white/50 mt-1">{readinessData.remaining.length} step{readinessData.remaining.length !== 1 ? "s" : ""} remaining</p>
                  )}
                  {readinessData.pct === 100 && (
                    <p className="text-[9px] text-emerald-300 font-medium mt-1">All prep complete — you're ready!</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Category Balance */}
          <div className="px-4 pt-2.5"><CategoryBalanceBar stops={itinerary.stops} /></div>

          {/* ═══ EXPIRATION WARNINGS ═══ */}
          {itinerary.expiration_warnings?.length > 0 && (
            <div className="px-4 pt-2" data-testid="expiration-warnings">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Expiration Warnings
                </p>
                {itinerary.expiration_warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-amber-800">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    <span><strong>{w.stop_name}</strong>: {w.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ TEMPO BADGE ═══ */}
          {itinerary.collection_tempo && (
            <div className="px-4 pt-2" data-testid="tempo-badge">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold border" style={{ borderColor: tc(themeColor, 0.3), color: themeColor, backgroundColor: tc(themeColor, 0.06) }}>
                <Calendar className="w-3 h-3" />
                {itinerary.collection_tempo === "one_big_day" && "One Big Day"}
                {itinerary.collection_tempo === "birthday_week" && "Birthday Week"}
                {itinerary.collection_tempo === "birthday_month" && "Birthday Month"}
                {itinerary.collection_tempo === "custom" && "Custom Schedule"}
              </div>
            </div>
          )}

          {/* ═══ INTERACTIVE MAP ═══ */}
          <div className="px-4 pt-2">
            <RoutePreviewMap stops={itinerary.stops} onToggleClaim={toggleClaim} onNavigate={openGoogleMaps} themeColor={themeColor} />
          </div>

          {/* ═══ REHEARSE YOUR BIRTHDAY + CHANGE PACE ═══ */}
          <div className="px-4 pt-3 space-y-2">
            <RehearseBirthday themeColor={themeColor} />
            <button
              onClick={() => setPaceSheetOpen(true)}
              className="w-full py-2.5 border-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition hover:bg-slate-50"
              style={{ borderColor: tc(themeColor, 0.3), color: themeColor }}
              data-testid="change-pace-btn"
            >
              <Gauge className="w-3.5 h-3.5" /> Change Pace
            </button>
          </div>

          {/* Browse & Add */}
          <div className="px-4 pt-2">
            <button onClick={() => setShowBrowse(true)} className="w-full py-3 border-2 border-dashed rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition hover:opacity-80" style={{ backgroundColor: tc(themeColor, 0.05), borderColor: tc(themeColor, 0.4), color: themeColor }} data-testid="browse-add-btn">
              <Plus className="w-4 h-4" /> Browse & Add Stops
            </button>
          </div>

          {/* Budget Breakdown (editable) */}
          {itinerary.out_of_pocket && (
            <div className="px-4 pt-2">
              <OutOfPocketEstimator outOfPocket={itinerary.out_of_pocket} totalSavings={itinerary.total_savings} themeColor={themeColor} />
            </div>
          )}

          {/* ═══ SCHEDULE YOUR DAY ═══ */}
          <div className="px-4 pt-4" data-testid="schedule-section">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4" style={{ color: themeColor }} />
              <p className="text-sm font-bold text-slate-700">Schedule Your Day</p>
            </div>
            {/* Tab bar */}
            <div className="flex gap-1.5 bg-slate-100 p-1 rounded-xl mb-3" data-testid="schedule-tab-bar">
              {[
                { id: "slots", label: "Drag & Slot", desc: "Easy" },
                { id: "timeline", label: "Timeline", desc: "Medium" },
                { id: "queue", label: "Smart Queue", desc: "Flexible" },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setScheduleTab(prev => prev === tab.id ? null : tab.id)}
                  className={`flex-1 py-2 px-2 rounded-lg text-center transition-all ${scheduleTab === tab.id ? "bg-white shadow-sm" : "hover:bg-white/50"}`}
                  data-testid={`schedule-tab-${tab.id}`}
                >
                  <span className={`text-xs font-bold block ${scheduleTab === tab.id ? "text-slate-800" : "text-slate-400"}`}>{tab.label}</span>
                  <span className={`text-[9px] ${scheduleTab === tab.id ? "text-sky-500" : "text-slate-300"}`}>{tab.desc}</span>
                </button>
              ))}
            </div>

            {/* Scheduler content */}
            <AnimatePresence mode="wait">
              {scheduleTab && (
                <motion.div
                  key={scheduleTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {scheduleTab === "slots" && <DragSlotScheduler itinerary={itinerary} themeColor={themeColor} />}
                  {scheduleTab === "timeline" && <TimelineScheduler itinerary={itinerary} themeColor={themeColor} />}
                  {scheduleTab === "queue" && <SmartQueueScheduler itinerary={itinerary} themeColor={themeColor} />}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ═══ ACCORDION SECTIONS ═══ */}
          <div className="px-4 pt-3 space-y-2" data-testid="accordion-sections">
            {/* TIMELINE */}
            <AccordionSection
              id="timeline" title="Timeline" icon={List} isOpen={openSection === "timeline"}
              onToggle={() => toggleSection("timeline")} themeColor={themeColor}
              badge={<span className="text-[9px] font-bold text-slate-400">{totalStops} stops</span>}
            >
              <TimelineContent
                itinerary={itinerary} activeDay={activeDay} setActiveDay={setActiveDay}
                moveStop={moveStop} toggleClaim={toggleClaim} removeStop={removeStop}
                navigate={navigate} getSegment={getSegment} themeColor={themeColor}
                city={city} state={state} prepDone={prepDone} setPrepDone={setPrepDone}
              />
            </AccordionSection>

            {/* GO MODE */}
            <AccordionSection
              id="go" title="Go Mode" icon={Zap} isOpen={openSection === "go"}
              onToggle={() => toggleSection("go")} themeColor={themeColor}
              badge={claimedCount > 0 ? <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">{claimedCount}/{totalStops}</span> : null}
            >
              <GoModeContent
                itinerary={itinerary} nextUnclaimed={nextUnclaimed}
                claimedCount={claimedCount} totalStops={totalStops}
                toggleClaim={toggleClaim} openGoogleMaps={openGoogleMaps}
                tripTimer={tripTimer} setTripTimer={setTripTimer}
                getEstMin={getEstMin} getSegment={getSegment}
                themeColor={themeColor}
              />
            </AccordionSection>

            {/* Pre-flight check is now integrated into Rehearse Your Birthday above */}
          </div>

          {/* ═══ SMART SUGGESTIONS ═══ */}
          {suggestions.length > 0 && (
            <div className="px-4 pt-4" data-testid="smart-suggestions">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="w-3.5 h-3.5" style={{ color: themeColor }} /><p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">You might also like</p></div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {suggestions.map(s => (
                  <div key={s.business_id} className="flex-shrink-0 w-36 bg-white border border-slate-100 rounded-xl p-2.5 cursor-pointer hover:shadow-sm transition" onClick={() => navigate(`/offer/${s.business_id}`)} data-testid={`suggestion-${s.business_id}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center mb-1.5 overflow-hidden">
                      {s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-4 h-4 text-slate-300" />}
                    </div>
                    <p className="text-xs font-bold text-slate-700 truncate">{s.name}</p>
                    <p className="text-[9px] text-slate-400 truncate">{s.offer_title}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] font-bold text-emerald-600">${s.offer_value_est || 0}</span>
                      <button onClick={(e) => { e.stopPropagation(); addSuggestion(s.business_id); }} disabled={addingStop === s.business_id} className="px-2 py-1 text-white text-[9px] font-bold rounded-lg flex items-center gap-0.5" style={{ backgroundColor: themeColor }} data-testid={`add-suggestion-${s.business_id}`}>
                        {addingStop === s.business_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Plus className="w-2.5 h-2.5" />} Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ SHARE & INVITE ═══ */}
          <div className="px-4 pt-4 space-y-2" data-testid="share-actions">
            {/* Hidden share image ref (for html2canvas) */}
            <div ref={shareRef} className="rounded-2xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${themeColor}, ${darken(themeColor, 50)})`, position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <p className="text-[10px] font-bold text-white/70 uppercase">My Birthday Plan</p>
              <p className="text-2xl font-black mt-1">${itinerary.total_savings} in Birthday Deals · {totalStops} stops</p>
              <div className="mt-2 space-y-1">{itinerary.stops.slice(0, 5).map(s => (<div key={s.id} className="flex items-center gap-2 bg-white/10 rounded-lg px-2 py-1"><span className="text-xs font-bold">{s.name}</span><span className="text-[10px] text-white/70 ml-auto">${s.value_est}</span></div>))}{itinerary.stops.length > 5 && <p className="text-[10px] text-white/70 text-center">+{itinerary.stops.length - 5} more</p>}</div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => exportToGoogleCalendar(itinerary.stops, user?.birthday)} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 flex items-center justify-center gap-1.5" data-testid="export-calendar-btn"><CalendarPlus className="w-3.5 h-3.5 text-blue-500" /> Calendar</button>
              <button onClick={saveAsImage} disabled={savingImage} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 flex items-center justify-center gap-1.5" data-testid="save-image-btn">{savingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" style={{ color: themeColor }} />} Image</button>
              <button onClick={copyShareText} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 flex items-center justify-center gap-1.5" data-testid="share-card-btn"><Copy className="w-3.5 h-3.5 text-violet-500" /> Copy</button>
            </div>
          </div>

          {/* Invite Friends */}
          <div className="px-4 pt-3 pb-2" data-testid="invite-section">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Invite Friends & Family</p>
            <div className="grid grid-cols-4 gap-2">
              {/* QR Code */}
              <button onClick={async () => {
                if (!shareLink) await generateShareLink();
                setShowQR(true);
              }} disabled={sharing} className="py-3 bg-white border border-slate-200 rounded-xl text-center hover:bg-slate-50 flex flex-col items-center gap-1.5" data-testid="qr-btn">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="19" y="14" width="2" height="2"/><rect x="14" y="19" width="2" height="2"/><rect x="19" y="19" width="2" height="2"/></svg>
                <span className="text-[9px] font-bold text-slate-500">QR Code</span>
              </button>
              {/* Share (native) */}
              <button onClick={async () => {
                const link = shareLink || await generateShareLink().then(() => shareLink);
                const url = shareLink || `${window.location.origin}/shared-itinerary/`;
                if (navigator.share) {
                  try { await navigator.share({ title: "My BirthdayDeaLight Plan", text: `Check out my birthday itinerary - $${itinerary.total_savings} in free stuff!`, url }); } catch {}
                } else { await copyToClipboard(url); toast.success("Link copied!"); }
              }} className="py-3 bg-white border border-slate-200 rounded-xl text-center hover:bg-slate-50 flex flex-col items-center gap-1.5" data-testid="native-share-btn">
                <ExternalLink className="w-5 h-5 text-slate-600" />
                <span className="text-[9px] font-bold text-slate-500">Share</span>
              </button>
              {/* Copy Link */}
              <button onClick={async () => {
                if (!shareLink) await generateShareLink();
                else { await copyToClipboard(shareLink); toast.success("Link copied!"); }
              }} disabled={sharing} className="py-3 bg-white border border-slate-200 rounded-xl text-center hover:bg-slate-50 flex flex-col items-center gap-1.5" data-testid="copy-link-btn">
                {sharing ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Link2 className="w-5 h-5 text-slate-600" />}
                <span className="text-[9px] font-bold text-slate-500">Copy Link</span>
              </button>
              {/* Copy Text */}
              <button onClick={copyShareText} className="py-3 bg-white border border-slate-200 rounded-xl text-center hover:bg-slate-50 flex flex-col items-center gap-1.5" data-testid="copy-text-btn">
                <Copy className="w-5 h-5 text-slate-600" />
                <span className="text-[9px] font-bold text-slate-500">Copy Text</span>
              </button>
            </div>
            {shareLink && <p className="text-[9px] text-slate-400 text-center mt-2 truncate">Link: {shareLink}</p>}
          </div>

          {/* QR Code Modal */}
          <AnimatePresence>
            {showQR && shareLink && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center" onClick={() => setShowQR(false)} data-testid="qr-modal">
                <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl p-6 mx-4 max-w-sm text-center" onClick={e => e.stopPropagation()}>
                  <h3 className="text-lg font-black text-slate-800 mb-3">Scan to Join</h3>
                  <div className="bg-white p-4 rounded-xl border border-slate-100 inline-block">
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareLink)}`} alt="QR Code" className="w-48 h-48" data-testid="qr-image" />
                  </div>
                  <p className="text-xs text-slate-500 mt-3">Friends can scan this to see your birthday plan</p>
                  <button onClick={() => setShowQR(false)} className="mt-3 px-6 py-2 text-white text-sm font-bold rounded-xl" style={{ backgroundColor: themeColor }} data-testid="qr-close">Done</button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Edit Plan */}
          <div className="px-4 pt-1 pb-2">
            <button onClick={() => setShowFilterModal(true)} className="w-full py-3 border-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition hover:opacity-80" style={{ borderColor: tc(themeColor, 0.3), color: themeColor }} data-testid="edit-plan-btn">
              <SlidersHorizontal className="w-4 h-4" /> Edit My Plan
            </button>
          </div>
        </>
      )}

      {/* ═══ FILTER BOTTOM SHEET MODAL ═══ */}
      <AnimatePresence>
        {showFilterModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/40" onClick={() => setShowFilterModal(false)} data-testid="filter-modal-backdrop">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="absolute bottom-0 w-full bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="filter-modal">
              <div className="flex justify-center pt-3 pb-2"><div className="w-10 h-1 bg-slate-200 rounded-full" /></div>
              <div className="px-5 pb-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-black text-slate-800">Edit Plan</h2>
                  <button onClick={() => setShowFilterModal(false)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="filter-close"><X className="w-5 h-5 text-slate-400" /></button>
                </div>

                {/* Location */}
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Location</p>
                  <div className="flex gap-2">
                    <input value={city} onChange={e => { setCity(e.target.value); setGpsCoords(null); }} placeholder="City" className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2" style={{ '--tw-ring-color': tc(themeColor, 0.3) }} data-testid="filter-city" />
                    <input value={state} onChange={e => setState(e.target.value)} placeholder="State" className="w-24 px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2" data-testid="filter-state" />
                    <button onClick={detectGPS} disabled={detectingGPS} className="px-3 py-2 bg-slate-100 rounded-xl hover:bg-slate-200 transition" data-testid="filter-gps">
                      {detectingGPS ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : <Navigation className="w-4 h-4 text-slate-500" />}
                    </button>
                  </div>
                </div>

                {/* Trip Length */}
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Trip Length</p>
                  <div className="flex gap-2">
                    {TRIP_TYPES.map(t => (
                      <button key={t.id} onClick={() => setTripType(t.id)} className="flex-1 py-2.5 rounded-xl text-center border transition" style={tripType === t.id ? { backgroundColor: tc(themeColor, 0.08), borderColor: tc(themeColor, 0.5), color: themeColor } : { borderColor: '#e2e8f0', color: '#64748b' }} data-testid={`trip-${t.id}`}>
                        <p className="text-xs font-bold">{t.label}</p><p className="text-[9px] opacity-60">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                  {(tripType === "multi_day" || tripType === "month") && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-slate-500">Days:</span>
                      <input type="range" min={tripType === "month" ? 7 : 2} max={tripType === "month" ? 30 : 10} value={numDays} onChange={e => setNumDays(parseInt(e.target.value))} className="flex-1" style={{ accentColor: themeColor }} data-testid="num-days-slider" />
                      <span className="text-sm font-bold w-6 text-center" style={{ color: themeColor }}>{numDays}</span>
                    </div>
                  )}
                </div>

                {/* Start Date */}
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">When do you want to start?</p>
                  <div className="flex gap-2 mb-2">
                    {[
                      { id: "birthday", label: "On my birthday", desc: "Most common" },
                      { id: "early", label: "A few days early", desc: "Pre-birthday deals" },
                      { id: "custom", label: "Pick a date", desc: "You choose" },
                    ].map(opt => {
                      const bdayStr = getNextBirthday();
                      const earlyStr = bdayStr ? new Date(new Date(bdayStr).getTime() - 3 * 86400000).toISOString().split("T")[0] : "";
                      const isSelected =
                        (opt.id === "birthday" && startDate === bdayStr) ||
                        (opt.id === "early" && startDate === earlyStr) ||
                        (opt.id === "custom" && startDate !== bdayStr && startDate !== earlyStr);
                      return (
                        <button
                          key={opt.id}
                          onClick={() => {
                            if (opt.id === "birthday") setStartDate(bdayStr);
                            else if (opt.id === "early") setStartDate(earlyStr);
                          }}
                          className="flex-1 py-2.5 rounded-xl text-center border transition"
                          style={isSelected ? { backgroundColor: tc(themeColor, 0.08), borderColor: tc(themeColor, 0.5), color: themeColor } : { borderColor: '#e2e8f0', color: '#64748b' }}
                          data-testid={`start-${opt.id}`}
                        >
                          <p className="text-xs font-bold">{opt.label}</p>
                          <p className="text-[9px] opacity-60">{opt.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': tc(themeColor, 0.3) }}
                    data-testid="start-date-input"
                  />
                  {startDate && (
                    <p className="text-[10px] text-slate-400 mt-1.5 text-center">
                      Starts{" "}
                      {new Date(startDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                      {startDate === getNextBirthday() ? " (your birthday!)" : ""}
                    </p>
                  )}
                </div>

                {/* Categories */}
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">What do you want?</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORY_OPTIONS.map(catOpt => {
                      const catId = catOpt.id;
                      const sel = selectedCategories.includes(catId);
                      const Icon = catOpt.icon;
                      const label = catOpt.label;
                      return (
                        <button key={catId} onClick={() => setSelectedCategories(prev => sel ? prev.filter(c => c !== catId) : [...prev, catId])} className="px-2.5 py-1.5 rounded-lg border text-xs font-semibold flex items-center gap-1.5 transition" style={sel ? { backgroundColor: tc(themeColor, 0.1), borderColor: tc(themeColor, 0.5), color: themeColor } : { borderColor: '#e2e8f0', color: '#94a3b8' }} data-testid={`cat-${catId.replace(/\s+/g, "-")}`}>
                          <Icon className="w-3 h-3" /> {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button onClick={generateItinerary} disabled={generating} className="w-full py-3.5 text-white text-sm font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg" style={{ backgroundColor: themeColor }} data-testid="generate-itinerary-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {generating ? "Building your day..." : itinerary ? "Regenerate Itinerary" : "Generate My Itinerary"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ RADIUS PICKER BOTTOM SHEET ═══ */}
      <AnimatePresence>
        {showRadiusPicker && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/40" onClick={() => setShowRadiusPicker(false)} data-testid="radius-picker-backdrop">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25 }} className="absolute bottom-0 w-full bg-white rounded-t-3xl pb-8" onClick={e => e.stopPropagation()} data-testid="radius-picker">
              <div className="flex justify-center pt-3 pb-2"><div className="w-10 h-1 bg-slate-200 rounded-full" /></div>
              <div className="px-5 pb-6 space-y-4">
                <h3 className="text-lg font-black text-slate-800">Search Radius</h3>
                <p className="text-xs text-slate-500">How far are you willing to drive for free stuff?</p>
                <div className="flex items-center gap-4">
                  <Crosshair className="w-5 h-5 text-slate-400" />
                  <input type="range" min={5} max={50} step={5} value={radiusValue} onChange={e => setRadiusValue(parseInt(e.target.value))} className="flex-1" style={{ accentColor: themeColor }} data-testid="radius-slider" />
                  <span className="text-xl font-black w-16 text-center" style={{ color: themeColor }}>{radiusValue}mi</span>
                </div>
                <div className="flex gap-2">
                  {[5, 10, 15, 25, 50].map(r => (
                    <button key={r} onClick={() => setRadiusValue(r)} className="flex-1 py-2 rounded-xl border text-xs font-bold transition" style={radiusValue === r ? { backgroundColor: tc(themeColor, 0.1), borderColor: themeColor, color: themeColor } : { borderColor: '#e2e8f0', color: '#64748b' }} data-testid={`radius-preset-${r}`}>{r}mi</button>
                  ))}
                </div>
                <button onClick={() => updateRadius(radiusValue)} className="w-full py-3 text-white text-sm font-bold rounded-xl" style={{ backgroundColor: themeColor }} data-testid="radius-apply">Apply Radius</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ BROWSE OFFERS PANEL ═══ */}
      {showBrowse && (
        <BrowseOffers
          selectedCategories={itinerary?.categories_selected || selectedCategories}
          themeColor={themeColor}
          onAdd={async (businessId) => {
            const res = await axios.post(`${API}/itinerary/add-stop`, { business_id: businessId });
            setItinerary(prev => ({
              ...prev,
              stops: [...(prev?.stops || []), res.data.stop],
              total_stops: (prev?.total_stops || 0) + 1,
              total_savings: res.data.total_savings,
            }));
          }}
          onClose={() => setShowBrowse(false)}
        />
      )}

      <BottomNav />
      <TooltipOverlay screen="itinerary" targetSelector='[data-testid="category-balance-bar"]' message="Your birthday itinerary! Drag stops to reorder, tap to claim as you go." />
      <ChangePaceSheet
        isOpen={paceSheetOpen}
        onClose={() => setPaceSheetOpen(false)}
        currentTempo={collectionTempo || itinerary?.collection_tempo}
        themeColor={themeColor}
        birthday={user?.birthday}
        onTempoChanged={(data) => {
          setItinerary(prev => ({
            ...prev,
            stops: data.stops,
            num_days: data.num_days,
            trip_type: data.trip_type,
            collection_tempo: data.collection_tempo,
          }));
          setCollectionTempo(data.collection_tempo);
          setNumDays(data.num_days);
          setTripType(data.trip_type);
          setPaceSheetOpen(false);
        }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ── TIMELINE CONTENT ──────────────────────────────────────────
// ════════════════════════════════════════════════════════════════
function TimelineContent({ itinerary, activeDay, setActiveDay, moveStop, toggleClaim, removeStop, navigate, getSegment, themeColor, city, state, prepDone, setPrepDone }) {
  const openGoogleMaps = (stop) => {
    const q = stop.lat && stop.lng ? `${stop.lat},${stop.lng}` : encodeURIComponent(`${stop.name} ${city} ${state}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank");
  };

  return (
    <div className="space-y-2" data-testid="timeline-view">
      {/* Multi-day selector */}
      {itinerary.num_days > 1 && (
        <div data-testid="week-mode-selector">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => setActiveDay(d => Math.max(1, d - 1))} disabled={activeDay <= 1} className="p-1 rounded-md bg-slate-100 text-slate-500 disabled:opacity-30" data-testid="prev-day-btn"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex-1 flex gap-1 overflow-x-auto">
              {Array.from({ length: itinerary.num_days }, (_, i) => i + 1).map(d => {
                const dayStops = itinerary.stops.filter(s => s.day === d);
                const dayComplete = dayStops.length > 0 && dayStops.every(s => s.claimed);
                return (
                  <button key={d} onClick={() => setActiveDay(d)} className={`flex-shrink-0 px-3 py-2 rounded-xl text-center border transition relative`} style={activeDay === d ? { backgroundColor: tc(themeColor, 0.08), borderColor: tc(themeColor, 0.5), color: themeColor } : dayComplete ? { backgroundColor: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' } : { borderColor: '#e2e8f0', color: '#64748b' }} data-testid={`week-day-${d}`}>
                    <p className="text-[10px] font-bold">Day {d}</p>
                    <p className="text-[8px] opacity-60">{dayStops.length} stops</p>
                    {dayComplete && <Award className="w-3 h-3 text-emerald-500 absolute -top-1 -right-1" />}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setActiveDay(d => Math.min(itinerary.num_days, d + 1))} disabled={activeDay >= itinerary.num_days} className="p-1 rounded-md bg-slate-100 text-slate-500 disabled:opacity-30" data-testid="next-day-btn"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Stops by time slot */}
      {(() => {
        const daysToShow = itinerary.num_days > 1 ? [activeDay] : Array.from(new Set(itinerary.stops.map(s => s.day))).sort();
        return daysToShow.map(day => (
          <div key={day}>
            {["morning", "lunch", "afternoon", "evening"].map(slot => {
              const slotStops = itinerary.stops.filter(s => s.day === day && s.time_slot === slot);
              if (slotStops.length === 0) return null;
              const sc = SLOT_COLORS[slot];
              return (
                <div key={slot} className="mb-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Clock className={`w-3 h-3 ${sc.text}`} />
                    <span className={`text-[10px] font-bold ${sc.text} uppercase tracking-wider`}>{sc.label}</span>
                    <span className="text-[9px] text-slate-400">{SLOT_TIMES[slot]}</span>
                  </div>
                  <div className="space-y-1.5 ml-5 border-l-2 border-dashed border-slate-200 pl-3">
                    {slotStops.map((stop, idx) => {
                      const globalIdx = itinerary.stops.indexOf(stop);
                      const brandColor = stop.brand_colors?.[0]?.hex;
                      return (
                        <div key={stop.id}>
                          <motion.div layout className={`rounded-xl border p-3 transition cursor-pointer ${stop.claimed ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-100"}`} data-testid={`stop-${stop.id}`} onClick={() => toggleClaim(stop.id)}>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-slate-100 flex items-center justify-center" style={brandColor ? { backgroundColor: `${brandColor}10` } : {}}>
                                {stop.logo_url ? <img src={stop.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-5 h-5 text-slate-300" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <h3 className="text-sm font-bold text-slate-800 truncate">{stop.name}</h3>
                                  {stop.claimed && <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                                  {!stop.claimed && getStatus(stop.business_id) && <UnifiedStatusBadge status={getStatus(stop.business_id)} />}
                                </div>
                                <p className="text-[10px] text-slate-500 line-clamp-1">{stop.offer_title}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] font-bold text-emerald-600">${stop.value_est}</span>
                                  <span className="text-[9px] text-slate-300">|</span>
                                  <span className="text-[9px] text-slate-400 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />{stop.ease_stars}</span>
                                  {stop.requires_app && <span className="text-[8px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">App</span>}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
                                <button onClick={() => moveStop(globalIdx, -1)} className="p-1 rounded-md bg-slate-50 text-slate-400 hover:bg-slate-100" data-testid={`move-up-${stop.id}`}><ChevronUp className="w-3 h-3" /></button>
                                <button onClick={() => removeStop(stop.id)} className="p-1 rounded-md bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500" data-testid={`remove-${stop.id}`}><X className="w-3 h-3" /></button>
                                <button onClick={() => moveStop(globalIdx, 1)} className="p-1 rounded-md bg-slate-50 text-slate-400 hover:bg-slate-100" data-testid={`move-down-${stop.id}`}><ChevronDown className="w-3 h-3" /></button>
                              </div>
                            </div>
                            {/* Peak Hour Warning */}
                            {stop.peak_warning && (
                              <button onClick={() => {
                                if (!stop.suggested_slot) return;
                                // This needs to update via parent - using a simple approach
                                stop.time_slot = stop.suggested_slot;
                                stop.peak_warning = false;
                                toggleClaim(stop.id); toggleClaim(stop.id); // force re-render hack
                              }} className="mt-2 w-full px-2.5 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 hover:bg-red-100 transition text-left" data-testid={`peak-warn-${stop.id}`}>
                                <Clock className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                                <span className="text-[9px] text-red-700 flex-1"><span className="font-bold">Peak alert:</span> {stop.peak_info?.reason}. Tap to move to <span className="font-bold">{stop.suggested_slot}</span>.</span>
                              </button>
                            )}
                            {/* Prep Action Pills */}
                            {(() => {
                              const pills = [];
                              if (stop.requires_app) pills.push({ key: `${stop.id}-app`, label: "Get App", action: "app", link: stop.website });
                              if (stop.signup_lead_days > 0) pills.push({ key: `${stop.id}-signup`, label: `Sign up ${stop.signup_lead_days}d ahead`, action: "signup", link: stop.website });
                              if (stop.purchase_required) pills.push({ key: `${stop.id}-purchase`, label: "Purchase req'd", action: "info" });
                              if (pills.length === 0) return null;
                              const done = prepDone || {};
                              return (
                                <div className="mt-2 flex flex-wrap gap-1" data-testid={`prep-pills-${stop.id}`}>
                                  {pills.map(p => {
                                    const isDone = done[p.key];
                                    return isDone ? (
                                      <span key={p.key} className="text-[8px] px-2 py-1 rounded-full border bg-emerald-50 border-emerald-300 text-emerald-600 flex items-center gap-1" data-testid={`prep-pill-${p.key}`}>
                                        <Check className="w-2.5 h-2.5" /> Done
                                      </span>
                                    ) : (
                                      <span key={p.key} className="inline-flex items-center gap-0.5" data-testid={`prep-pill-${p.key}`}>
                                        {p.link && p.action !== "info" ? (
                                          <a href={p.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                            className={`text-[8px] px-2 py-1 rounded-l-full border flex items-center gap-1 hover:opacity-80 ${p.action === "app" ? "bg-sky-50 border-sky-200 text-sky-600" : "bg-amber-50 border-amber-200 text-amber-600"}`}>
                                            <ExternalLink className="w-2.5 h-2.5" /> {p.label}
                                          </a>
                                        ) : (
                                          <span className={`text-[8px] px-2 py-1 rounded-l-full border flex items-center gap-1 ${p.action === "info" ? "bg-orange-50 border-orange-200 text-orange-600" : "bg-amber-50 border-amber-200 text-amber-600"}`}>
                                            {p.label}
                                          </span>
                                        )}
                                        <button onClick={async (e) => {
                                          e.stopPropagation();
                                          setPrepDone(prev => ({ ...prev, [p.key]: true }));
                                          try { await axios.post(`${API}/itinerary/stop/${stop.id}/prep`, { step_key: p.key, completed: true }); }
                                          catch { /* local state already updated */ }
                                        }}
                                          className="text-[7px] px-1.5 py-1 rounded-r-full border-y border-r bg-slate-50 border-slate-200 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
                                          title="Mark as done"
                                        >
                                          <Check className="w-2.5 h-2.5" />
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </motion.div>
                          {/* Backup alternatives */}
                          {itinerary.backup_stops?.[String(globalIdx)]?.length > 0 && (
                            <div className="ml-12 flex items-center gap-1.5 py-1" data-testid={`backup-${stop.id}`}>
                              <span className="text-[8px] text-slate-300 font-medium">Backups:</span>
                              {itinerary.backup_stops[String(globalIdx)].map((b, bi) => (
                                <button key={bi} onClick={() => navigate(`/offer/${b.business_id}`)} className="text-[8px] hover:underline" style={{ color: themeColor }}>{b.name}</button>
                              ))}
                            </div>
                          )}
                          {idx < slotStops.length - 1 && getSegment(globalIdx) && <TravelSegment segment={getSegment(globalIdx)} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ));
      })()}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ── GO MODE CONTENT ───────────────────────────────────────────
// ════════════════════════════════════════════════════════════════
function GoModeContent({ itinerary, nextUnclaimed, claimedCount, totalStops, toggleClaim, openGoogleMaps, tripTimer, setTripTimer, getEstMin, getSegment, themeColor }) {
  const tripRunning = tripTimer?.startedAt && !tripTimer?.endedAt;
  const tripDone = tripTimer?.startedAt && tripTimer?.endedAt;
  const tripElapsed = tripTimer?.startedAt ? ((tripTimer.endedAt || new Date()) - tripTimer.startedAt) : 0;

  return (
    <div className="space-y-2" data-testid="go-mode-view">
      {/* Trip Timer */}
      <div className="rounded-xl border-2 p-3 text-center" style={{ borderColor: tripRunning ? '#f59e0b' : tripDone ? '#10b981' : tc(themeColor, 0.3) }} data-testid="trip-timer">
        {!tripTimer ? (
          <button onClick={() => setTripTimer({ startedAt: new Date(), endedAt: null })} className="w-full py-3 text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 shadow-md" style={{ backgroundColor: '#10b981' }} data-testid="start-trip-btn">
            <Play className="w-4 h-4" /> Start Birthday Run!
          </button>
        ) : tripRunning ? (
          <div>
            <p className="text-[9px] text-amber-600 font-bold uppercase tracking-wider">Trip in progress</p>
            <motion.p animate={{ opacity: [1, 0.6, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} className="text-3xl font-mono font-black text-amber-600 mt-1" data-testid="trip-elapsed">
              {fmtTimer(tripElapsed)}
            </motion.p>
            <button onClick={() => setTripTimer(prev => ({ ...prev, endedAt: new Date() }))} className="mt-2 px-6 py-2 bg-red-500 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 mx-auto" data-testid="stop-trip-btn">
              <Square className="w-3 h-3" /> End Trip
            </button>
          </div>
        ) : (
          <div>
            <p className="text-[9px] text-emerald-600 font-bold uppercase tracking-wider">Trip complete!</p>
            <p className="text-3xl font-mono font-black text-emerald-600 mt-1" data-testid="trip-total">{fmtTimer(tripElapsed)}</p>
            <p className="text-[10px] text-slate-400 mt-1">{claimedCount}/{totalStops} stops claimed</p>
            <div className="flex gap-2 justify-center mt-2">
              <button onClick={() => setTripTimer(prev => ({ ...prev, endedAt: null }))} className="px-4 py-2 bg-amber-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5" data-testid="continue-trip-btn">
                <Play className="w-3 h-3" /> Continue
              </button>
              <button onClick={() => { setTripTimer(prev => prev); window.scrollTo({ top: 0, behavior: "smooth" }); setOpenSection(null); }} className="px-4 py-2 bg-slate-200 text-slate-600 text-xs font-bold rounded-lg flex items-center gap-1.5" data-testid="end-trip-confirmed">
                <Square className="w-3 h-3" /> Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Next up banner */}
      {nextUnclaimed && (
        <div className="rounded-xl p-3 text-white" style={{ background: `linear-gradient(to right, #10b981, #059669)` }} data-testid="next-up-banner">
          <p className="text-[9px] font-bold text-emerald-200 uppercase tracking-wider">Next Stop</p>
          <div className="flex items-center gap-3 mt-1">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center overflow-hidden">
              {nextUnclaimed.logo_url ? <img src={nextUnclaimed.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-5 h-5 text-white/70" />}
            </div>
            <div className="flex-1">
              <p className="font-bold text-sm">{nextUnclaimed.name}</p>
              <p className="text-xs text-emerald-100">{nextUnclaimed.offer_title} · ${nextUnclaimed.value_est}</p>
            </div>
            <button onClick={() => openGoogleMaps(nextUnclaimed)} className="px-3 py-2 bg-white text-emerald-600 text-xs font-bold rounded-lg flex items-center gap-1.5 hover:bg-emerald-50" data-testid="navigate-btn">
              <ExternalLink className="w-3 h-3" /> Go
            </button>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="bg-emerald-50 rounded-xl border border-emerald-200 px-3 py-2.5 flex items-center gap-2">
        <PartyPopper className="w-5 h-5 text-emerald-500" />
        <div className="flex-1"><p className="text-xs font-bold text-emerald-700">Birthday Go Mode</p><p className="text-[10px] text-emerald-500">Check off deals as you claim them!</p></div>
        <span className="text-lg font-black text-emerald-700">{claimedCount}/{totalStops}</span>
      </div>
      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full" animate={{ width: `${totalStops > 0 ? (claimedCount / totalStops) * 100 : 0}%` }} transition={{ type: "spring", stiffness: 100 }} />
      </div>

      {/* Stop list (simplified - no per-stop timers) */}
      <div className="space-y-1.5">
        {itinerary.stops.map((stop, i) => {
          const isCurrentStop = stop.id === nextUnclaimed?.id;
          return (
            <div key={stop.id}>
              <motion.div className={`rounded-xl border transition ${stop.claimed ? "bg-emerald-50 border-emerald-300" : isCurrentStop ? "bg-white border-emerald-400 ring-2 ring-emerald-200" : "bg-white border-slate-200"}`} data-testid={`go-stop-${stop.id}`}>
                <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => toggleClaim(stop.id)}>
                  <button className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition ${stop.claimed ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`} data-testid={`go-claim-${stop.id}`}>
                    {stop.claimed && <Check className="w-4 h-4 text-white" />}
                  </button>
                  <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-slate-50 flex items-center justify-center">
                    {stop.logo_url ? <img src={stop.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} /> : <Gift className="w-4 h-4 text-slate-300" />}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className={`text-sm font-bold ${stop.claimed ? "line-through text-slate-400" : "text-slate-800"}`}>{stop.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{stop.offer_title}</p>
                  </div>
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <span className="text-xs font-bold text-emerald-600">${stop.value_est}</span>
                    <button onClick={() => openGoogleMaps(stop)} className="p-1.5 rounded-lg bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-500 transition" data-testid={`nav-${stop.id}`}>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </motion.div>
              {i < itinerary.stops.length - 1 && getSegment(i) && <TravelSegment segment={getSegment(i)} />}
            </div>
          );
        })}
      </div>

      {/* Completion Summary */}
      {claimedCount === totalStops && totalStops > 0 && (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-4 text-white text-center" data-testid="completion-summary">
          <Trophy className="w-10 h-10 mx-auto mb-2" />
          <p className="text-lg font-black">Birthday Deal Champion!</p>
          <p className="text-sm text-amber-100 mt-1">${itinerary.total_savings} claimed across {totalStops} stops</p>
          {tripDone && <p className="text-sm text-amber-100 mt-1">Completed in {fmtTimer(tripElapsed)}</p>}
        </motion.div>
      )}
    </div>
  );
}
